import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useEntityProfile } from '@/features/profiles/hooks/useEntityProfile';
import type { StartupProfileData } from '@/features/profiles/api/startup-profile.api';
import { ROUTES } from '@/lib/constants/routes';
import { COMPLETION_FIELD_LABELS, DASHBOARD_PREVIEW, DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';
import type { ActiveMembership } from '@/lib/types/auth.types';

const S = DASHBOARD_STRINGS.COMPLETION;

export interface DashboardProfileCompletenessProps {
  /** The startup the member is acting for. */
  membership: ActiveMembership;
}

/**
 * How complete the member's Startup Profile is and what is still missing
 * (MYN-254). Both come from the Backend's own completeness rule, so they match
 * the admin console. Renders nothing while loading, on error, or once the
 * profile is complete: it is a nudge, not page furniture.
 */
export function DashboardProfileCompleteness({ membership }: DashboardProfileCompletenessProps) {
  const { data } = useEntityProfile(membership);
  const completion = (data as StartupProfileData | undefined)?.completion;
  if (!completion || completion.pct >= 100) return null;

  const shown = completion.missingFields.slice(0, DASHBOARD_PREVIEW.MISSING_FIELDS);
  const remaining = completion.missingFields.length - shown.length;

  return (
    <Card shadow="soft" hoverEffect={false} className="w-full p-6!">
      <section aria-labelledby="dashboard-completion-heading" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="dashboard-completion-heading" className="text-h4 text-teal">
            {S.TITLE}
          </h2>
          <span className="text-sm font-semibold text-navy">{S.PCT_LABEL(completion.pct)}</span>
        </div>
        <div
          role="progressbar"
          aria-label={S.PROGRESS_LABEL}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={completion.pct}
          className="h-2 w-full overflow-hidden rounded-full bg-navy/10"
        >
          <div className="h-full rounded-full bg-green" style={{ width: `${completion.pct}%` }} />
        </div>
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <p className="m-0 text-sm text-slate">
            {S.MISSING_LEAD} {shown.map((field) => COMPLETION_FIELD_LABELS[field]).join(', ')}
            {remaining > 0 && ` ${S.MORE(remaining)}`}
          </p>
          <Button asChild variant="green" className="shrink-0 px-5! py-2.5! text-sm">
            <Link to={ROUTES.STARTUP_PROFILE}>{S.CTA}</Link>
          </Button>
        </div>
      </section>
    </Card>
  );
}
